import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Code2,
  Database,
  Wrench,
  Palette,
  Play,
  Download,
  Copy,
  Check,
  Search,
  FileJson,
  Layers,
  Zap,
  Settings
} from 'lucide-react'
import { CodeDemo } from '../components/ui/CodeDemo'

interface DocSection {
  id: string
  title: string
  icon: React.ElementType
  description: string
}

const getDocSections = (t: any): DocSection[] => [
  {
    id: 'installation',
    title: t('sections.installation.title'),
    icon: Download,
    description: t('sections.installation.description')
  },
  {
    id: 'setup',
    title: t('sections.setup.title'),
    icon: Settings,
    description: t('sections.setup.description')
  },
  {
    id: 'visual-builder',
    title: t('sections.visualBuilder.title'),
    icon: Palette,
    description: t('sections.visualBuilder.description')
  },
  {
    id: 'json-config',
    title: t('sections.jsonConfig.title'),
    icon: FileJson,
    description: t('sections.jsonConfig.description')
  },
  {
    id: 'table-config',
    title: t('sections.tableConfig.title'),
    icon: Database,
    description: t('sections.tableConfig.description')
  },
  {
    id: 'components',
    title: t('sections.components.title'),
    icon: Layers,
    description: t('sections.components.description')
  }
]

const installationCode = `# Install FormStudio library
npm install @smartdata/formstudio
# or
yarn add @smartdata/formstudio`

const setupCode = `// Initialize FormStudio client
import { FormStudioClient } from '@smartdata/formstudio'

const client = new FormStudioClient({
  apiUrl: process.env.FORMSTUDIO_API_URL,
  apiKey: process.env.FORMSTUDIO_API_KEY,
  tenant: 'your-company'
})`

const formCreationCode = `// Create dynamic form configuration
const categoryForm = await client.forms.create({
  entityType: 'categorias',
  programName: 'Categorias',
  programIcon: 'category',
  complexity: 'SIMPLE',
  tabs: [{
    name: 'dados-gerais',
    label: 'DADOS',
    icon: 'info',
    fields: [
      {
        name: 'nome',
        label: 'Nome',
        type: 'TEXT',
        component: 'q-input',
        placeholder: 'Digite o nome da categoria',
        required: true,
        cols: 'col-12',
        validations: ['required']
      },
      {
        name: 'imagemDeCapa',
        label: 'Imagem de Capa', 
        type: 'FILE',
        component: 'file-upload',
        cols: 'col-12'
      },
      {
        name: 'showWeb',
        label: 'Show Web',
        type: 'CHECKBOX', 
        component: 'q-checkbox',
        cols: 'col-3'
      }
    ]
  }]
})`

const jsonConfigCode = `{
  "entityType": "categorias",
  "programName": "Categorias",
  "programIcon": "o_category",
  "tableOrder": "nome ASC",
  "complexity": "SIMPLE",
  "tabs": [
    {
      "name": "dados-gerais",
      "label": "DADOS",
      "icon": "info",
      "fields": [
        {
          "name": "nome",
          "label": "Nome",
          "type": "TEXT",
          "component": "q-input",
          "placeholder": "Digite o nome da categoria",
          "required": true,
          "cols": "col-12",
          "validations": ["required"]
        }
      ]
    }
  ]
}`

const tableConfigCode = `// Configure table columns and actions
await client.tables.configure('categorias', {
  columns: [
    { name: 'id', label: 'ID', sortable: true, width: 60 },
    { name: 'nome', label: 'Nome', sortable: true, width: 400 },
    { name: 'showMenuWeb', label: 'Show Menu Web', width: 150 }
  ],
  actions: [
    { type: 'ADD', label: 'Adicionar', icon: 'add' },
    { type: 'EDIT', label: 'Editar', icon: 'edit' },
    { type: 'DELETE', label: 'Excluir', icon: 'delete' }
  ]
})`

const componentsCode = `// Render forms in your Vue/React application
import { FormStudioForm, FormStudioTable } from '@smartdata/formstudio'

// In your component
<template>
  <div>
    <!-- Dynamic Form Component -->
    <FormStudioForm 
      :entity-type="'categorias'"
      :form-config="formConfig"
      @save="handleSave"
      @cancel="handleCancel"
    />
    
    <!-- Dynamic Table with Actions -->
    <FormStudioTable
      :entity-type="'categorias'"
      :columns="tableConfig.columns"
      :actions="tableConfig.actions"
      @action="handleAction"
    />
  </div>
</template>

<script setup>
const formConfig = await client.forms.getConfig('categorias')
const tableConfig = await client.tables.getConfig('categorias')
</script>`

export const DocsPage: React.FC = () => {
  const { t } = useTranslation('docs')
  const docSections = getDocSections(t)
  const [selectedSection, setSelectedSection] = useState(docSections[0])
  const [searchTerm, setSearchTerm] = useState('')
  const [copiedCode, setCopiedCode] = useState('')

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(''), 2000)
  }

  const filteredSections = docSections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const renderCodeSection = (code: string, filename: string, language: string, copyId: string) => (
    <div className="bg-gray-900 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 bg-gray-800">
        <span className="text-gray-400 text-sm font-mono">{filename}</span>
        <button
          onClick={() => copyToClipboard(code, copyId)}
          className="flex items-center gap-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm text-gray-300 transition-colors"
        >
          {copiedCode === copyId ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copiedCode === copyId ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      <div className="max-h-96 overflow-auto">
        <CodeDemo code={code} language={language} />
      </div>
    </div>
  )

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-kylix-primary to-blue-900 text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              {t('hero.title')}
              <span className="block bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                {t('hero.subtitle')}
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-200 mb-8 leading-relaxed"
            >
              {t('hero.description')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center"
            >
              <a 
                href="/contact"
                className="px-8 py-4 rounded-lg font-semibold bg-white text-kylix-primary hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-lg"
              >
                <Play className="w-5 h-5" />
                {t('hero.getInfoButton')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('navigation.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kylix-secondary focus:border-transparent"
                  />
                </div>

                {/* Navigation */}
                <nav className="space-y-2">
                  {filteredSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setSelectedSection(section)}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                        selectedSection.id === section.id
                          ? 'bg-kylix-background text-kylix-primary'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <section.icon className="w-5 h-5" />
                      <div>
                        <div className="font-semibold">{section.title}</div>
                        <div className="text-xs text-gray-500">{section.description}</div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <motion.div
                key={selectedSection.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-8"
              >
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="p-3 bg-kylix-background rounded-xl">
                    <selectedSection.icon className="w-8 h-8 text-kylix-primary" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{selectedSection.title}</h2>
                    <p className="text-gray-600">{selectedSection.description}</p>
                  </div>
                </div>

                {/* Installation Section */}
                {selectedSection.id === 'installation' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('content.installation.title')}</h3>
                      {renderCodeSection(installationCode, 'install.sh', 'bash', 'install')}
                    </div>
                  </div>
                )}

                {/* Setup Section */}
                {selectedSection.id === 'setup' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('content.setup.title')}</h3>
                      {renderCodeSection(setupCode, 'setup.js', 'javascript', 'setup')}
                    </div>
                  </div>
                )}

                {/* Visual Builder Section */}
                {selectedSection.id === 'visual-builder' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('content.visualBuilder.title')}</h3>
                      
                      {/* Main Interface */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.visualBuilder.mainInterface.title')}</h4>
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/1-main.png" 
                            alt="FormStudio Main Interface"
                            className="w-full h-auto"
                          />
                        </div>
                        <p className="text-gray-600 mt-4" dangerouslySetInnerHTML={{ __html: t('content.visualBuilder.mainInterface.description') }}></p>
                      </div>

                      {/* Form Builder */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.visualBuilder.formBuilder.title')}</h4>
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/FormBuilder.png" 
                            alt="FormStudio Form Builder"
                            className="w-full h-auto"
                          />
                        </div>
                        <p className="text-gray-600 mt-4">{t('content.visualBuilder.formBuilder.description')}</p>
                      </div>

                      {/* Drag & Drop */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.visualBuilder.dragDrop.title')}</h4>
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/DragDrop.png" 
                            alt="FormStudio Drag Drop"
                            className="w-full h-auto"
                          />
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h5 className="font-semibold text-green-800 mb-2">{t('content.visualBuilder.dragDrop.fieldLibrary')}</h5>
                            <p className="text-sm text-green-700">{t('content.visualBuilder.dragDrop.fieldLibraryDesc')}</p>
                          </div>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 className="font-semibold text-blue-800 mb-2">{t('content.visualBuilder.dragDrop.propertiesPanel')}</h5>
                            <p className="text-sm text-blue-700">{t('content.visualBuilder.dragDrop.propertiesPanelDesc')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* JSON Configuration Section */}
                {selectedSection.id === 'json-config' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('content.jsonConfig.title')}</h3>
                      
                      {/* JSON Editor Screenshot */}
                      <div className="mb-8">
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/EditorJson.png" 
                            alt="FormStudio JSON Editor"
                            className="w-full h-auto"
                          />
                        </div>
                        <p className="text-gray-600 mt-4" dangerouslySetInnerHTML={{ __html: t('content.jsonConfig.editorDescription') }}></p>
                      </div>

                      {/* Code Example */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.jsonConfig.configExample')}</h4>
                        {renderCodeSection(jsonConfigCode, 'form-config.json', 'json', 'json')}
                      </div>

                      {/* Programmatic Creation */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.jsonConfig.programmaticCreation')}</h4>
                        {renderCodeSection(formCreationCode, 'create-form.js', 'javascript', 'create')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Table Configuration Section */}
                {selectedSection.id === 'table-config' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('content.tableConfig.title')}</h3>
                      
                      {/* Table Config Screenshot */}
                      <div className="mb-8">
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/TableConfig.png" 
                            alt="FormStudio Table Configuration"
                            className="w-full h-auto"
                          />
                        </div>
                        <p className="text-gray-600 mt-4">{t('content.tableConfig.tableDescription')}</p>
                      </div>

                      {/* Programs Management */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.tableConfig.programsManagement')}</h4>
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/Program.png" 
                            alt="FormStudio Program Management"
                            className="w-full h-auto"
                          />
                        </div>
                        <p className="text-gray-600 mt-4">{t('content.tableConfig.programsDescription')}</p>
                      </div>

                      {/* Table Code */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.tableConfig.codeConfig')}</h4>
                        {renderCodeSection(tableConfigCode, 'table-config.js', 'javascript', 'table')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Components Section */}
                {selectedSection.id === 'components' && (
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('content.components.title')}</h3>
                      
                      {/* New Tab Modal */}
                      <div className="mb-8">
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.components.modalSupport')}</h4>
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                          <img 
                            src="/images/formstudio/NewTab.png" 
                            alt="FormStudio Modal Support"
                            className="w-full h-auto"
                          />
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h5 className="font-semibold text-green-800 mb-2">{t('content.components.modalCreation')}</h5>
                            <p className="text-sm text-green-700">{t('content.components.modalCreationDesc')}</p>
                          </div>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 className="font-semibold text-blue-800 mb-2">{t('content.components.inlineEditing')}</h5>
                            <p className="text-sm text-blue-700">{t('content.components.inlineEditingDesc')}</p>
                          </div>
                        </div>
                      </div>

                      {/* Components Code */}
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-4">{t('content.components.componentImplementation')}</h4>
                        {renderCodeSection(componentsCode, 'components.vue', 'javascript', 'components')}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              {t('features.title')}
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Palette, key: 'zeroCode' },
              { icon: Zap, key: 'realtime' },
              { icon: Database, key: 'databaseDriven' },
              { icon: Layers, key: 'responsive' },
              { icon: Code2, key: 'typeSafe' },
              { icon: Wrench, key: 'enterprise' }
            ].map((feature, index) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-kylix-background rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-kylix-primary" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{t(`features.items.${feature.key}.title`)}</h3>
                <p className="text-gray-600 text-center text-sm">{t(`features.items.${feature.key}.description`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Start */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 mb-6"
            >
              {t('readyToStart.title')}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gray-900 rounded-xl overflow-hidden max-w-md mx-auto"
            >
              <div className="px-6 py-4 bg-gray-800">
                <span className="text-gray-400 text-sm font-mono">{t('readyToStart.terminal')}</span>
              </div>
              <div className="p-6">
                <code className="text-green-400 text-lg">npm install @smartdata/formstudio</code>
                <div className="text-gray-400 text-sm mt-2">{t('readyToStart.comment')}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}